// -----------------------------------------------------------------------------
// pdfPlan.ts — floor-plan PDFs in the browser (v0.6.0 Part 6), using pdf.js v4.10.38 vendored in
// public/vendor/pdfjs/ (loaded only when a plan is shown; see its README.txt).
//   openPdf(source)            → { pages, render(page, canvas, cssWidth) }   source = File / ArrayBuffer / URL
//   Security: isEvalSupported false (pdf.js never compiles code from a PDF's fonts) — plans come from staff,
//   but a PDF is still outside data.
//   pdf.geometry(page)         → lines, circles and text of the page as drawn (v0.7.0 gridline detection, grid.ts)
// Used by: PlanUpload (A6.7 page count + preview), PlanCanvas (A4 plan view, A5.2 place pin), GridSetup (A6.4).
// -----------------------------------------------------------------------------
import { geometryFromOps, type OpsTable, type PageGeometry, type TextItem } from "../modules/issues/grid";

const BASE = "/vendor/pdfjs/";

interface PdfViewport { width: number; height: number; transform: number[] }
interface PdfPage { rotate: number; getViewport(o: { scale: number; rotation?: number }): PdfViewport; render(o: object): { promise: Promise<void>; cancel(): void };
  getOperatorList(): Promise<{ fnArray: number[]; argsArray: unknown[] }>;
  getTextContent(): Promise<{ items: { str?: string; transform?: number[]; width?: number; height?: number }[] }> }
interface PdfDoc { numPages: number; getPage(n: number): Promise<PdfPage>; destroy(): Promise<void> }
interface PdfJs { GlobalWorkerOptions: { workerSrc: string }; getDocument(o: object): { promise: Promise<PdfDoc> }; OPS: OpsTable;
  Util: { transform(a: number[], b: number[]): number[] } }

let lib: Promise<PdfJs> | null = null;
function pdfjs(): Promise<PdfJs> {
  if (!lib) {
    const url = BASE + "pdf.min.mjs";                       // a variable: the bundler leaves this import to the browser
    lib = (import(/* @vite-ignore */ url) as Promise<PdfJs>).then((m) => { m.GlobalWorkerOptions.workerSrc = BASE + "pdf.worker.min.mjs"; return m; });
    lib.catch(() => { lib = null; });
  }
  return lib;
}

export interface OpenPdf {
  pages: number;
  /** Draw page n (1-based) to fill `cssWidth` CSS pixels (sharp on high-DPI screens). Returns the page's height / width.
   *  sharp > 1 draws more pixels for a zoomed-in view (A6.4), capped at about 16 million pixels.
   *  turn (v0.7.1, A6.7): extra clockwise quarter turns chosen at upload (plans.rotation). */
  render(n: number, canvas: HTMLCanvasElement, cssWidth: number, sharp?: number, turn?: number): Promise<number>;
  close(): void;
  /** Lines, bubbles and text of page n as drawn (scale 1, the page's own rotation + `turn` applied) — for gridline detection (A6.4). */
  geometry(n: number, turn?: number): Promise<PageGeometry>;
}

export async function openPdf(source: File | ArrayBuffer | string): Promise<OpenPdf> {
  const m = await pdfjs();
  const data = source instanceof File ? await source.arrayBuffer() : source;
  const doc = await m.getDocument(typeof data === "string" ? { url: data, isEvalSupported: false } : { data: new Uint8Array(data), isEvalSupported: false }).promise;
  return {
    pages: doc.numPages,
    async render(n, canvas, cssWidth, sharp = 1, turn = 0) {
      const page = await doc.getPage(Math.min(Math.max(1, n), doc.numPages));
      const rotation = rotated(page.rotate, turn);
      const base = page.getViewport({ scale: 1, rotation });
      const ratio = base.height / base.width;
      // at most ~16 million canvas pixels (a zoomed A1 sheet would otherwise need hundreds of MB)
      const dpr = Math.min(Math.sqrt(16e6 / (cssWidth * cssWidth * ratio)), Math.min(2, window.devicePixelRatio || 1) * Math.max(1, sharp));
      const vp = page.getViewport({ scale: (cssWidth / base.width) * dpr, rotation });
      canvas.width = Math.round(vp.width); canvas.height = Math.round(vp.height);
      canvas.style.width = `${cssWidth}px`; canvas.style.height = `${Math.round(cssWidth * ratio)}px`;
      await page.render({ canvasContext: canvas.getContext("2d")!, viewport: vp }).promise;
      return ratio;
    },
    close() { doc.destroy().catch(() => {}); },
    async geometry(n, turn = 0) {
      const page = await doc.getPage(Math.min(Math.max(1, n), doc.numPages));
      const vp = page.getViewport({ scale: 1, rotation: rotated(page.rotate, turn) });
      const [ops, text] = await Promise.all([page.getOperatorList(), page.getTextContent()]);
      const { segs, circles } = geometryFromOps(ops.fnArray, ops.argsArray, m.OPS, vp.transform);
      const texts: TextItem[] = [];
      for (const t of text.items) {
        if (!t.str?.trim() || !t.transform) continue;
        const tr = m.Util.transform(vp.transform, t.transform);
        texts.push({ s: t.str.trim(), x: tr[4], y: tr[5], w: t.width ?? 0, h: Math.hypot(tr[2], tr[3]), rot: Math.atan2(tr[1], tr[0]) });
      }
      return { width: vp.width, height: vp.height, segs, circles, texts };
    },
  };
}

/** The page's own /Rotate plus the turn chosen at upload, as pdf.js wants it (0 / 90 / 180 / 270). Pure — unit-tested. */
export const rotated = (own: number, turn: number) => ((((own || 0) + (turn || 0)) % 360) + 360) % 360;

/** ⟲ / ⟳ in the A6.7 preview: a quarter turn left (-1) or right (+1). Pure — unit-tested. */
export const turnBy = (turn: number, dir: -1 | 1) => rotated(turn, dir * 90) as 0 | 90 | 180 | 270;

/** Pin position (0–1 across / down the plan page) from a click on the drawn plan. Pure — unit-tested. */
export function pinFromClick(clientX: number, clientY: number, box: { left: number; top: number; width: number; height: number }): { x: number; y: number } | null {
  const x = (clientX - box.left) / box.width, y = (clientY - box.top) / box.height;
  if (!(x >= 0 && x <= 1 && y >= 0 && y <= 1)) return null;
  return { x: Math.round(x * 100000) / 100000, y: Math.round(y * 100000) / 100000 };
}

/** Next plan version label after the current one ("V01" → "V02"; none → "V00"). Pure — unit-tested. */
export const nextPlanVersion = (current: string | null | undefined) =>
  current ? `V${String(Number(current.replace(/\D/g, "")) + 1).padStart(2, "0")}` : "V00";
